<template>
  <v-dialog v-model="showDialog" max-width="480" persistent>
    <v-card>
      <v-card-title class="text-h6">
        Add Variant<span v-if="productName"> to "{{ productName }}"</span>
      </v-card-title>
      <v-card-text>
        <ColorAutocomplete
          v-model="form.color"
          :items="colorItems"
          outlined
          dense
          prepend-inner-icon="mdi-palette"
          :error-messages="errors.color"
          @created="(color) => colorItems.push(color)"
        >
          <template v-slot:label>Color (optional)</template>
        </ColorAutocomplete>
        <v-text-field
          v-model="form.size"
          label="Size (optional)"
          outlined
          dense
          :error-messages="errors.size"
        ></v-text-field>
        <v-text-field
          v-model.number="form.price"
          type="number"
          min="0"
          outlined
          dense
          :error-messages="errors.price"
        >
          <template v-slot:label>Price <span style="color:#f44336;font-weight:bold;">*</span></template>
        </v-text-field>
        <v-text-field
          v-model.number="form.discount_percentage"
          type="number"
          min="0"
          max="100"
          label="Discount % (optional)"
          outlined
          dense
        ></v-text-field>
        <v-file-input
          v-model="form.image"
          label="Image"
          outlined
          dense
          accept="image/*"
          prepend-icon=""
          prepend-inner-icon="mdi-camera"
          :error-messages="errors.image"
        >
          <template v-slot:label>Image (optional)</template>
        </v-file-input>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn text @click="close" :disabled="isSaving">Cancel</v-btn>
        <v-btn color="primary" :loading="isSaving" @click="save">Add Variant</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import ColorAutocomplete from "@/components/ColorAutocomplete.vue";

export default {
  name: "QuickAddVariantDialog",
  components: { ColorAutocomplete },
  props: {
    show: Boolean,
    productId: [Number, String],
    productName: String,
    initialColor: { type: String, default: "" },
  },
  data() {
    return {
      form: {
        color: "",
        size: "",
        price: null,
        discount_percentage: null,
        image: null,
      },
      // Master > Colors, as { id, text, value } rows.
      colorItems: [],
      errors: {},
      isSaving: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
  },
  watch: {
    async show(value) {
      if (value) {
        await this.getColors();
        this.form = {
          color: this.matchColor(this.initialColor),
          size: "",
          price: null,
          discount_percentage: null,
          image: null,
        };
        this.errors = {};
      }
    },
  },
  methods: {
    async getColors() {
      try {
        const response = await axios.get("masters?context=colorAndsize");
        this.colorItems = (response.data.colors || []).map((color) => ({
          id: color.id,
          text: color.description,
          value: color.code,
        }));
      } catch (e) {
        console.log(e);
      }
    },
    // The text typed into the PO Variant search pre-selects a color, but only
    // when it names one that already exists in Master > Colors.
    matchColor(text) {
      const query = (text || "").trim().toUpperCase();
      if (!query) return null;
      const match = this.colorItems.find(
        (item) => (item.text || "").toUpperCase() === query || (item.value || "").toUpperCase() === query
      );
      return match ? match.value : null;
    },
    close() {
      if (!this.isSaving) {
        this.$emit("update:show", false);
      }
    },
    async save() {
      this.errors = {};
      if (!this.form.price && this.form.price !== 0) {
        this.errors = { ...this.errors, price: "Price is required." };
      }
      if (Object.keys(this.errors).length) {
        return;
      }

      this.isSaving = true;
      try {
        const payload = new FormData();
        if (this.form.color) payload.append("color", this.form.color);
        if (this.form.size) payload.append("size", this.form.size);
        payload.append("price", this.form.price);
        if (this.form.discount_percentage) {
          payload.append("discount_percentage", this.form.discount_percentage);
        }
        if (this.form.image) payload.append("images[0][image_path]", this.form.image);

        const response = await axios.post(`/products/${this.productId}/variants`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.$emit("created", response.data.data);
        this.$emit("update:show", false);
      } catch (e) {
        console.log(e);
        if (e.response && e.response.data && e.response.data.errors) {
          this.errors = e.response.data.errors;
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
